/**
 * Resolves a Sentry sample rate from a build-time environment variable.
 *
 * The browser SDK's decision is propagated to the API in the `sentry-trace` header, and
 * the Laravel SDK honours that parent decision — so this rate, not the backend's
 * `SENTRY_TRACES_SAMPLE_RATE`, is what governs whether a browser-initiated request is
 * traced. Raising it temporarily is how you get a trace for a specific slow request.
 *
 * Vite inlines `VITE_*` at build time, so a change still needs a redeploy; what this
 * avoids is having to edit code to make one.
 *
 * Anything missing or unparseable falls back rather than reading as 0, since a typo that
 * silently turns tracing off is the expensive failure. Rates are clamped to the 0–1 that
 * Sentry accepts.
 */
export const resolveSampleRate = (
  configured: string | undefined,
  fallback: number,
): number => {
  if (configured === undefined || configured.trim() === "") {
    return fallback;
  }

  const rate = Number(configured);

  if (!Number.isFinite(rate)) {
    return fallback;
  }

  return Math.min(Math.max(rate, 0), 1);
};
